import { selectGridSelection } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.selector';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import { selectFacility } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import { unitLicenseGridColumnsAndOr } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitRequirements/UnitGridLicenseHelper';
import { formatOrderLicenseList } from '@AMIEWEB/Order/OrderCreationForm/helper';
import { CompactStatus, IOrderLicense } from '@AMIEWEB/Order/Store/Order.redux';
import { selectOrderRequirementTypes } from '@AMIEWEB/Order/Store/OrderCreation.selectors';
import { GridApi, GridPinnedRowsProp, GridSelectionModel } from '@mui/x-data-grid-pro';
import { Grid } from '@mui/material';
import { CollectionType } from 'app/enums/Common';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme, props) => ({
  root: {
    '& .MuiGrid-root': {
      margin: 0,
      width: 'auto',
    },
    '& .MuiGrid-root > .MuiGrid-item': {
      padding: 0,
    },
    '& .MuiDataGrid-root': {
      border: 'none',
      borderRadius: 0,
    },
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
      border: 'none !important',
      outline: 'none !important',
      backgroundColor: 'transparent !important',
    },
  },
  licenseGrid: {
    opacity: 1,
  },
  overlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    whiteSpace: 'nowrap',
  },
}));

export const OrderLicenseGrid = ({
  filteredLicenses,
  skillSetIndex,
  groupIndex,
  orderLicenseOptions,
}: {
  filteredLicenses: any[];
  skillSetIndex: number;
  groupIndex: number;
  orderLicenseOptions: any[];
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext();
  const gridApi = React.useRef<GridApi>();
  const [licenseColumns, setLicenseColumns] = useState([]);
  const [dataList, setDataList] = useState<any[]>([]);
  const facilityData = useSelector(selectFacility);
  const { states } = useSelector(selectOrderRequirementTypes);

  const [orderSelectionModel, setOrderSelectionModel] = useState<any[]>([]);

  const skillsetLicenseData = getValues(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.LicensesIndex}].requirements`,
  );

  const selectedRowDetails = useRef<{ [id: string]: boolean }>();
  const setSelectedRowDetails = data => {
    selectedRowDetails.current = data;
  };

  const getPinnedRows = useCallback(() => {
    const pinnedRowIds = selectedRowDetails.current
      ? Object.keys(selectedRowDetails.current)
          .filter(key => selectedRowDetails.current[key] === true)
          .map(Number)
      : [];

    const pinnedRows: GridPinnedRowsProp = {
      top: dataList.filter(obj => pinnedRowIds.includes(obj.id)),
    };
    return pinnedRows;
  }, [skillsetLicenseData, dataList, selectedRowDetails, filteredLicenses]);

  useEffect(() => {
    if (filteredLicenses?.length && skillsetLicenseData) {
      const formattedGridData = formatOrderLicenseList(skillsetLicenseData, filteredLicenses);
      setDataList(formattedGridData);
      const filteredLicensesArray = skillsetLicenseData?.filter(x =>
        filteredLicenses?.some(y => y?.requirementTypeId === x?.licenseId),
      );
      const selectedLicenseIds = filteredLicensesArray?.map(x => x.licenseId);
      setOrderSelectionModel(selectedLicenseIds);
      const prepinnedRows = filteredLicensesArray?.reduce((acc, curr) => {
        if (curr?.licenseId) {
          acc[curr?.licenseId] = true;
        }
        return acc;
      }, {});
      setSelectedRowDetails(prepinnedRows);
    }
  }, [filteredLicenses, skillsetLicenseData]);

  useEffect(() => {
    if (filteredLicenses && skillsetLicenseData && dataList) {
      setLicenseColumns([
        ...unitLicenseGridColumnsAndOr(groupIndex, skillSetIndex, t, selectedRowDetails.current, setSelectedRowDetails),
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillSetIndex, selectedRowDetails, dataList, skillsetLicenseData, filteredLicenses, orderSelectionModel]);

  const onSelectionModelChange = useCallback(
    (selectionModel: GridSelectionModel) => {
      const filterLicenses = selectionModel?.filter(x => filteredLicenses?.some(y => y?.requirementTypeId === x));
      const filteredListIds = filteredLicenses?.map(item => item?.id);

      const remainingSelelctedOrderLicense = skillsetLicenseData?.filter(x => !filteredListIds?.includes(x?.licenseId));

      let licenseDetailsIndex: number = 0;

      if (filterLicenses && filterLicenses?.length > 0 && filteredLicenses?.length > 0) {
        const filteredLicensesArray = skillsetLicenseData?.filter(x =>
          orderLicenseOptions?.some(y => y?.requirementTypeId === x?.licenseId),
        );
        const orderLicensesDetails: IOrderLicense[] = [...filteredLicensesArray];

        filterLicenses.forEach(selectedOption => {
          licenseDetailsIndex = orderLicensesDetails?.findIndex(data => data.licenseId === selectedOption);

          if (licenseDetailsIndex === -1) {
            const selectedLicenseOptionDetails = filteredLicenses?.find(
              data => data?.requirementTypeId === selectedOption || data?.requirementTypeID === selectedOption,
            );
            const facilityState = states?.find(
              (state: any) =>
                state?.value === facilityData?.facilityResponse?.location?.state ||
                state?.value === facilityData?.response?.location?.state,
            );
            const licenseStateData = [
              {
                state: facilityState,
                isRequired: true, // "isRequired" always true
                documentAttachmentRequired: false,
              },
            ];
            if (selectedLicenseOptionDetails) {
              const selectedLicenseDetail: IOrderLicense = {
                licenseDescription: selectedLicenseOptionDetails.credentialName,
                licenseAbbreviation: selectedLicenseOptionDetails.abbreviation,
                licenseId:
                  selectedLicenseOptionDetails.requirementTypeId || selectedLicenseOptionDetails.requirementTypeID,
                isError: { compactStatus: false, state: false },
                isSelected: true,
                licenseStateDetails: selectedLicenseOptionDetails?.compact
                  ? [{ isRequired: true, documentAttachmentRequired: false }]
                  : licenseStateData,
                compactStatus: selectedLicenseOptionDetails?.compact ? CompactStatus.compact : CompactStatus.single,
              };
              orderLicensesDetails.push(selectedLicenseDetail);
            }
          }
        });

        const filteredData = orderLicensesDetails?.filter(licenseDetails =>
          filterLicenses.includes(licenseDetails?.licenseId),
        );

        const totalLicenseSelected = [...remainingSelelctedOrderLicense, ...filteredData];
        const totalLicenseSelectedIds = totalLicenseSelected?.map(x => x.licenseId);
        setOrderSelectionModel([...totalLicenseSelectedIds]);

        setValue(
          `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.LicensesIndex}].requirements`,
          licenseDetailsIndex === -1 ? orderLicensesDetails : totalLicenseSelected,
        );
      } else {
        setValue(
          `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.LicensesIndex}].requirements`,
          [],
        );
      }
    },
    [filteredLicenses, skillsetLicenseData],
  );

  return (
    <div data-testid="license-dropdown" className={classes.licenseGrid}>
          <Grid className={classes.root}>
            <XGrid
              apiRef={gridApi}
              hideFooterSelectedRowCount
              checkboxSelection
              disableSelectionOnClick
              gridTag={'Licenses'}
              height={500}
              rowHeight={58}
              columns={licenseColumns}
              onSelectionModelChange={onSelectionModelChange}
              selectionModel={orderSelectionModel}
              keepNonExistentRowsSelected={true}
              rows={dataList}
              toolbar={true}
              title={'License'}
              autoHeight={false}
              pagination={false}
              hideFooterPagination
              hideFooter={true}
              disablePaggination
              displayCustomCount={orderSelectionModel?.length}
              paginationProps={{
                hideResultsPerPageSelection: true,
                hidePageCount: true,
              }}
              disableVirtualization={true}
              components={{
                NoRowsOverlay: () => <div className={classes.overlay}>{t('No licenses match your search')}</div>,
              }}
              toolbarProps={{
                refreshButton: true,
                resetButton: true,
                refreshGrid: () => {},
                resetColumns: () =>
                  setLicenseColumns([
                    ...unitLicenseGridColumnsAndOr(
                      groupIndex,
                      skillSetIndex,
                      t,
                      selectedRowDetails,
                      setSelectedRowDetails,
                    ),
                  ]),
              }}
              pinnedRows={getPinnedRows()}
              experimentalFeatures={{ rowPinning: true }}
            />
          </Grid>
    </div>
  );
};
